<script setup lang="tsx">
import type { ComponentPublicInstance } from 'vue';

import { onMounted, ref, useTemplateRef, watch } from 'vue';

import HomeGreetings from '../components/HomeGreetings.vue';
import SetPassword from '../components/SetPassword.vue';
import { useAlert } from '../composables/useAlert';
import { useAppState } from '../store/ocg-app-state';
import { getServerMode, setPasswordRequested } from '../supabase';
import { hasMethod } from '../utils/hasMethod';
import SingleForm from './SingleForm.vue';
import OrganizationsTab from './SuperAdmin/OrganizationsTab.vue';
import SuperAdminTab from './SuperAdmin/SuperAdminTab.vue';
import UsersTab from './SuperAdmin/UsersTab.vue';

const appState = useAppState();
const alert = useAlert();

const setPassword
  = useTemplateRef<InstanceType<typeof SetPassword>>('setPassword');

const serverMode = ref<string | null>(null);

const tab = ref<string | null>(null);
type VueElement = Element | ComponentPublicInstance | null;
const tabs = ref(new Map<string, VueElement>());

function tabRef(tabName: string): (el: VueElement) => void {
  return el => tabs.value.set(tabName, el);
}

watch(tab, (newTab) => {
  if (!newTab) return;
  const newTabRef = tabs.value.get(newTab);
  if (hasMethod(newTabRef, 'load')) newTabRef.load();
});

onMounted(async () => {
  getServerMode().then((mode) => {
    serverMode.value = mode;
  });

  watch(
    () => !!setPasswordRequested.value,
    (newValue) => {
      if (newValue) {
        setPasswordRequested.value = false;
        setPassword.value?.open();
      }
    },
    { immediate: true },
  );
});

const tabDefinitions = [
  { key: 'superadmins', label: 'Superadmins', component: SuperAdminTab },
  { key: 'organizations', label: 'Organizations', component: OrganizationsTab },
  { key: 'users', label: 'Users', component: UsersTab },
];
</script>

<template>
  <SingleForm v-if="appState.currentUserMode === 'superadmin'" width="100%">
    <SetPassword ref="setPassword" />

    <!-- Displaying a personalized greeting based on the time of day.
    Time of day is calculated in script below -->
    <HomeGreetings :username="appState.currentUserName" />

    <h2 class="mb-3">Set up your server</h2>
    <!-- <div style="text-align: center;">Set up your server:</div> -->

    <v-card class="d-flex flex-column flex-1 min-h-0 overflow-hidden">
      <v-tabs v-model="tab" color="white" align-tabs="center" class="flex-shrink-0">
        <v-tab
          v-for="tabDef in tabDefinitions"
          :key="tabDef.key"
          :value="tabDef.key"
        >
          {{ tabDef.label }}
        </v-tab>
      </v-tabs>

      <v-card-text class="flex-1 min-h-0 overflow-y-auto">
        <v-tabs-window v-model="tab">
          <v-tabs-window-item
            v-for="tabDef in tabDefinitions"
            :key="tabDef.key"
            :value="tabDef.key"
          >
            <component
              :is="tabDef.component"
              :ref="tabRef(tabDef.key)"
              @error="(msg) => alert.error(msg)"
            />
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
    </v-card>
  </SingleForm>
  <SingleForm v-else>
    <div style="text-align: center">
      <h2>Welcome to OCG!</h2>
      <h4 v-if="serverMode !== 'operation'">
        This server is in <strong>{{ serverMode }}</strong> mode.
      </h4>

      <p>
        Please log-in with a superadmin account to access the superadmin
        interface!
      </p>
    </div>
  </SingleForm>
</template>
